<script setup>
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableRowRounded from "@/components/global-themed/table/TableRowRounded.vue";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";
import { useAdminKitPages } from "@/composables/admin/useAdminKitPages";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import useUser from "@/composables/useUser";
import TooltipQuestion from "@/components/global-themed/TooltipQuestion.vue";
import Swal from 'sweetalert2';

const props = defineProps({
  websiteId: {
    type: Number,
    required: true,
  },
});

const { kitPages, isPending, implementFixesMutation, resetKitPageMutation } = useAdminKitPages(props.websiteId);
const {isDeveloper} = useUser();

const {mutate: implementFixes } = implementFixesMutation();
const {mutate: resetKitPage } = resetKitPageMutation();

const handleResetPage = (pageId) => {
  Swal.fire({
    title: 'Are you sure?',
    text: 'Resetting this page will put it back in available pages for Kit to work on in the next cycle.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, reset it!',
    cancelButtonText: 'Cancel',
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      resetKitPage(pageId);
    }
  });
};
</script>

<template>
  <DataTable class="tw-w-full">
    <TableHeaderRow class="tw-grid-cols-16">
      <TableHeaderCell col-span="5">
        <span>Page Url</span>
      </TableHeaderCell>

      <TableHeaderCell col-span="3">
        <span class="tw-flex tw-gap-2 tw-items-center">
          Available fixes count
          <TooltipQuestion
            text="This shows the number of actions Kit can fix, not the total number of actions. That's why it may differ from the Actions Overview."
          />
        </span>
      </TableHeaderCell>

      <TableHeaderCell col-span="2">
        <span>Keywords count</span>
      </TableHeaderCell>

      <TableHeaderCell col-span="2" />

      <TableHeaderCell col-span="4" />
    </TableHeaderRow>

    <div
      v-if="isPending"
      class="tw-bg-white tw-p-8 tw-rounded-lg"
    >
      <LoadingBlock />
    </div>

    <div
      v-else
      class="tw-flex tw-flex-col tw-gap-4 tw-text-neutral-900 tw-text-base tw-mt-8"
    >
      <ZeroContent v-if="kitPages?.length < 1">
        Looks like there are no pages with Kit fixes available.
      </ZeroContent>

      <TableRowRounded
        v-for="page in kitPages"
        :key="page.id"
        class="tw-grid-cols-16"
      >
        <TableCell
          col-span="5"
          class="tw-flex tw-items-center tw-gap-4"
        >
          {{ page.page_url }}
        </TableCell>

        <TableCell col-span="3">
          {{ page.kit_fixable_actions_count }}
        </TableCell>

        <TableCell col-span="2">
          {{ page.keywords_count }}
        </TableCell>

        <TableCell col-span="2">
          <div class="tw-flex tw-justify-center">
            <div
              v-if="page.kit_page_priority"
              v-tooltip="{
                content: `The user has set the priority for this page to ${page.kit_page_priority}`,
                placement: 'bottom',
                theme: 'dark-blue-tooltip',
              }"
              class="tw-flex tw-justify-center tw-py-1 tw-w-10 tw-rounded-full tw-bg-primary tw-text-white"
            >
              {{ page.kit_page_priority }}
            </div>
          </div>
        </TableCell>

        <TableCell col-span="4">
          <div class="tw-flex tw-items-center tw-gap-4 tw-justify-end">
            <svg-vue
              v-if="page.kit_page_processed_at"
              v-tooltip="{
                content: `Kit has previously optimised this page on ${page.kit_page_processed_at}`,
                placement: 'bottom',
                theme: 'dark-blue-tooltip',
              }"
              class="tw-w-8 tw-text-success-700 tw-cursor-pointer"
              icon="icons/complete"
            />

            <DefaultButton
              v-if="page.kit_page_processed_at"
              v-tooltip="{
                content: 'Resetting this page will put it back in available pages for Kit to work on in the next cycle',
                placement: 'bottom',
                theme: 'dark-blue-tooltip',
              }"
              variant="danger"
              size="small"
              @click="handleResetPage(page.id)"
            >
              Reset
            </DefaultButton>

            <DefaultButton
              v-if="isDeveloper"
              size="small"
              @click="implementFixes(page.id)"
            >
              Implement fixes
            </DefaultButton>
          </div>
        </TableCell>
      </TableRowRounded>
    </div>
  </DataTable>
</template>
